import React, { cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { computePlacement, tooltipGroup, CLOSE_GRACE_MS } from '../../utils/tooltipLogic';

const ORIGIN = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/** True when a React node renders any visible text (so it does not need an aria-label). */
const hasVisibleText = (node) => {
    if (node == null || typeof node === 'boolean') return false;
    if (typeof node === 'string' || typeof node === 'number') return String(node).trim() !== '';
    if (Array.isArray(node)) return node.some(hasVisibleText);
    return isValidElement(node) ? hasVisibleText(node.props.children) : false;
};

const compose = (theirs, ours) => (event) => {
    if (typeof theirs === 'function') theirs(event);
    ours(event);
};

const focusVisible = (el) => {
    try {
        return el.matches(':focus-visible');
    } catch (err) {
        return true;
    }
};

/**
 * Fast, accessible tooltip that replaces the slow native `title` attribute.
 *
 * @param {object} props
 * @param {string} props.label Tooltip text. Also used as aria-label when the child is icon-only and has none.
 * @param {'top'|'bottom'|'left'|'right'} [props.side='top'] Preferred side; flips when it would leave the viewport.
 * @param {React.ReactElement} props.children A single focusable element. A disabled `<button>` is wrapped in an
 *   inline-flex span so it can still receive hover.
 *
 * Opens after 150 ms of hover, instantly on keyboard focus, and instantly when another tooltip was open in the
 * last 500 ms. Closes on leave, blur, Escape, scroll and press. Hovering the tooltip keeps it open.
 */
const Tooltip = ({ label, side = 'top', children }) => {
    const id = useId();
    const tipRef = useRef(null);
    const triggerRef = useRef(null);
    const timers = useRef({ open: null, close: null });
    const isOpen = useRef(false);
    const [open, setOpen] = useState(false);
    const [animate, setAnimate] = useState(true);
    const [pos, setPos] = useState(null);
    const [entered, setEntered] = useState(false);

    const clearTimers = () => {
        clearTimeout(timers.current.open);
        clearTimeout(timers.current.close);
    };

    const hide = useCallback(() => {
        clearTimers();
        if (isOpen.current) {
            isOpen.current = false;
            tooltipGroup.closed();
            setOpen(false);
            setPos(null);
            setEntered(false);
        }
    }, []);

    const show = (reason, target) => {
        triggerRef.current = target;
        clearTimers();
        if (isOpen.current) return;
        const { delay, skipped } = tooltipGroup.delayFor(reason);
        const run = () => {
            isOpen.current = true;
            tooltipGroup.opened();
            setAnimate(!skipped);
            setOpen(true);
        };
        if (delay === 0) run();
        else timers.current.open = setTimeout(run, delay);
    };

    const leave = () => {
        clearTimeout(timers.current.open);
        clearTimeout(timers.current.close);
        timers.current.close = setTimeout(hide, CLOSE_GRACE_MS);
    };

    const place = useCallback(() => {
        const trigger = triggerRef.current;
        const tip = tipRef.current;
        if (!trigger || !tip) return;
        const r = trigger.getBoundingClientRect();
        const t = tip.getBoundingClientRect();
        setPos(
            computePlacement({
                trigger: { top: r.top, left: r.left, width: r.width, height: r.height },
                tip: { width: t.width, height: t.height },
                viewport: { width: window.innerWidth, height: window.innerHeight },
                side,
            })
        );
    }, [side]);

    useLayoutEffect(() => {
        if (open) place();
    }, [open, label, place]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => e.key === 'Escape' && hide();
        const onScroll = (e) => !(tipRef.current && tipRef.current.contains(e.target)) && hide();
        document.addEventListener('keydown', onKey);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', place);
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', place);
        };
    }, [open, hide, place]);

    useEffect(() => {
        if (!open || !pos || !animate) return undefined;
        const raf = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(raf);
    }, [open, pos, animate]);

    useEffect(
        () => () => {
            clearTimers();
            if (isOpen.current) {
                isOpen.current = false;
                tooltipGroup.closed();
            }
        },
        []
    );

    if (!label || !isValidElement(children)) return children;

    const child = children;
    const p = child.props;
    const wrap = child.type === 'button' && p.disabled;
    const handlers = {
        onPointerEnter: (e) => e.pointerType !== 'touch' && show('hover', e.currentTarget),
        onPointerLeave: (e) => e.pointerType !== 'touch' && leave(),
        onFocus: (e) => focusVisible(e.target) && show('focus', e.currentTarget),
        onBlur: hide,
        onPointerDown: hide,
        onClick: hide,
    };
    const merged = {};
    Object.keys(handlers).forEach((k) => {
        merged[k] = compose(wrap ? undefined : p[k], handlers[k]);
    });

    const needsName = !p['aria-label'] && !p['aria-labelledby'] && !hasVisibleText(p.children);
    const trigger = cloneElement(child, {
        ...(wrap ? {} : merged),
        'aria-describedby': open ? [p['aria-describedby'], id].filter(Boolean).join(' ') : p['aria-describedby'],
        ...(needsName ? { 'aria-label': label } : {}),
    });

    const visible = pos && (!animate || entered);
    const tip =
        open &&
        createPortal(
            <div
                ref={tipRef}
                id={id}
                role="tooltip"
                onPointerEnter={() => clearTimeout(timers.current.close)}
                onPointerLeave={leave}
                style={{
                    position: 'fixed',
                    top: pos ? pos.top : 0,
                    left: pos ? pos.left : 0,
                    visibility: pos ? 'visible' : 'hidden',
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'scale(1)' : 'scale(0.96)',
                    transformOrigin: ORIGIN[pos ? pos.side : side],
                    transition: animate
                        ? 'opacity var(--duration-fast) var(--ease-out), transform var(--duration-fast) var(--ease-out)'
                        : 'none',
                }}
                className="pointer-events-auto z-[100] w-max max-w-[16rem] break-words rounded-chip bg-inverse px-2 py-1 text-xs font-medium text-canvas shadow-raised"
            >
                {label}
            </div>,
            document.body
        );

    return (
        <>
            {wrap ? (
                <span className="inline-flex" {...merged}>
                    {trigger}
                </span>
            ) : (
                trigger
            )}
            {tip}
        </>
    );
};

export default Tooltip;
